import { BrandIcon, Icon } from '@/components/ui'
import type { BrandName, IconName } from '@/components/ui'
import { Combobox } from './Combobox'
import type { Vocabulary } from './vocabulary'
import styles from './fields.module.css'

interface ChoiceFieldProps {
  id: string
  value: string
  vocabulary: Vocabulary
  onChange: (next: string) => void
}

/**
 * One value chosen from a known set.
 *
 * The control is the same either way; what changes is whether a value
 * outside the set can be committed.
 *
 * A **closed** vocabulary takes nothing new. An icon name with no path
 * behind it draws nothing at all, so there is no such thing as a new icon
 * you can name into existence.
 *
 * An **open** one accepts one, because naming a new domain from inside the
 * project that needs it beats being sent to another screen. The cost is a
 * reference pointing at nothing, so the field says so plainly — the site's
 * own response to a dangling reference is to render nothing, which reads as
 * a broken page rather than a typo in one field.
 */
export const ChoiceField = ({
  id,
  value,
  vocabulary,
  onChange,
}: ChoiceFieldProps) => {
  const { options, open, preview, manageAt, danglingWarning } = vocabulary

  const known = options.some((option) => option.value === value)
  const dangling = value !== '' && !known

  /* Drawn in the list as well as beside the field: choosing between forty
     glyphs by name alone is guesswork. */
  const glyph = preview
    ? (name: string) =>
        preview === 'brand' ? (
          <BrandIcon name={name as BrandName} size={18} />
        ) : (
          <Icon name={name as IconName} size={18} />
        )
    : undefined

  return (
    <div className={styles.choice}>
      <Combobox
        id={id}
        value={value}
        options={options}
        allowNew={open}
        placeholder={open ? 'Choose or type a new one' : 'Choose one'}
        {...(glyph ? { glyph } : {})}
        onCommit={onChange}
      />

      {dangling && danglingWarning ? (
        <p className={styles.warning}>{danglingWarning}</p>
      ) : null}

      {!dangling && manageAt ? (
        <p className={styles.hint}>
          Managed under <code>{manageAt}</code>
        </p>
      ) : null}
    </div>
  )
}
